import { useState, useRef, FormEvent, useEffect } from 'react';
import { Search } from 'lucide-react';
import { useFetcher } from '@remix-run/react';
import { NewsSearchModal, NewsSearchItem } from './NewsSearchModal';

// 타임아웃을 위한 타입
type TimeoutId = ReturnType<typeof setTimeout>;

// 광고 소스 타입 정의
type AdSource = 'default' | 'finance' | 'realestate';

// 검색 API 응답 타입 
interface SearchNewsResponse {
  action: string;
  results: NewsSearchItem[];
}

interface NewsSearchProps {
  adSource?: AdSource;
}

export function NewsSearch({ adSource = 'default' }: NewsSearchProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [searchResults, setSearchResults] = useState<NewsSearchItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const searchFetcher = useFetcher<SearchNewsResponse>();
  const debounceTimeout = useRef<TimeoutId | null>(null);

  // 광고 소스에 따른 버튼 색상 결정
  const getButtonStyle = () => {
    switch (adSource) {
      case 'finance':
        return {
          bgColor: 'bg-gray-900',
          hoverColor: 'hover:bg-black'
        };
      case 'realestate':
        return {
          bgColor: 'bg-black',
          hoverColor: 'hover:bg-gray-800'
        };
      default:
        return {
          bgColor: 'bg-[#b98e6a]',
          hoverColor: 'hover:bg-[#8b6b4f]'
        };
    }
  };

  const { bgColor, hoverColor } = getButtonStyle();

  // 검색어 변경 핸들러
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value;
    setSearchQuery(query);
    
    // 입력값이 없으면 검색 중지
    if (!query.trim()) {
      if (debounceTimeout.current) {
        clearTimeout(debounceTimeout.current);
      }
      return;
    }
    
    // 디바운스 적용하여 검색 요청
    if (debounceTimeout.current) {
      clearTimeout(debounceTimeout.current);
    }
    
    // 검색어가 2글자 이상인 경우에만 자동 검색
    if (query.trim().length >= 2) {
      setIsLoading(true);
      
      debounceTimeout.current = setTimeout(() => {
        searchFetcher.submit(
          { query, action: 'search_news' },
          { method: 'post', action: '/api/search-news' }
        );
      }, 300);
    }
  };

  // 검색 제출 핸들러
  const handleSearchSubmit = (e: FormEvent) => {
    e.preventDefault();
    
    if (!searchQuery.trim()) return;
    
    // 현재 진행 중인 디바운스 요청 취소
    if (debounceTimeout.current) {
      clearTimeout(debounceTimeout.current);
    }
    
    setIsLoading(true);
    setIsModalOpen(true);
    
    searchFetcher.submit(
      { query: searchQuery, action: 'search_news' },
      { method: 'post', action: '/api/search-news' }
    );
  };

  // 모달 닫기 핸들러
  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  // fetcher 상태 변경 감지
  useEffect(() => {
    if (searchFetcher.state === 'submitting') {
      setIsLoading(true);
    } else if (searchFetcher.state === 'idle' && searchFetcher.data) {
      setIsLoading(false);
      
      if (searchFetcher.data.action === 'search_news') {
        setSearchResults(searchFetcher.data.results || []);
      }
    }
  }, [searchFetcher.state, searchFetcher.data]);

  return (
    <>
      {/* 검색 폼 */}
      <form onSubmit={handleSearchSubmit} className="w-full">
        <div className="flex flex-col sm:flex-row gap-2 bg-white/90 p-2 rounded-lg shadow-lg">
          <input
            type="text"
            placeholder="검색어를 입력하세요"
            className="flex-1 px-4 py-2 text-gray-900 bg-transparent outline-none"
            value={searchQuery}
            onChange={handleSearchChange}
          />
          <button
            type="submit"
            className={`px-6 py-2 ${bgColor} ${hoverColor} text-white rounded-md flex items-center justify-center gap-2 transition-colors duration-200`}
          >
            <Search className="w-5 h-5" />
            <span>검색</span>
          </button>
        </div>
      </form>
      
      {/* 검색 결과 모달 */}
      <NewsSearchModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        searchResults={searchResults}
        searchQuery={searchQuery}
        isLoading={isLoading}
        adSource={adSource}
      />
    </>
  );
} 